/* External */
import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { Redirect } from 'react-router-dom';

/* Components */
import LiveContent from './liveContent/LiveContent';
import SeriesDetails from '../components/seriesDetails/SeriesDetails';
import VodContent from './vodContent/VodContent';
import NavBarPdp from '../navBarPdp/NavBarPdp';
import { useGtmContext } from '../components/GtmContext';
import Footer from '../components/footer/Footer';

/* Styles */
import './Pdp.scss';

/* Other */
import { history } from '../state/configureStore';
import {
  PATHS, PDP_ASSET_TYPES, PREVIOUS_URL, LANDER_URL,
  isUserLoggedIn,
  PUBLIC_PDP_ASSET_TYPES,
  getUserLanguageFromEnvironmentPublicPdp,
  IS_BRAZIL,
  REGIONS,
  savePdpUrlToStorage,
  PROFILES_STORAGE,
} from '../utils';
import { createBackendServicePublic } from '../services/backend';
import { useIsResponsive } from '../hooks/useIsResponsive';

function getRegionPublicPage() {
  if (IS_BRAZIL) {
    return REGIONS.BR;
  }

  return REGIONS.SSLA;
}

function Pdp({
  match: {
    params: { country: countryQuery, assetId, assetType },
  },
}) {
  const languageEnvironment = getUserLanguageFromEnvironmentPublicPdp(countryQuery);
  const isLoggedIn = isUserLoggedIn();
  const isMobile = useIsResponsive('(max-width: 1024px)');
  const isPublic = !isLoggedIn;
  const { search, pathname } = useLocation();
  const [dataPublic, setDataPublic] = useState({});
  const containerRef = useRef();
  const queryParams = new URLSearchParams(search);
  const startTimeParam = queryParams.get('startTime');
  const regionPublic = isPublic ? getRegionPublicPage() : '';
  const {
    setGtmContent,
    setGtmPlaySource,
    setGtmSectionName,
    setGtmComponentType,
    setGtmCarouselVerticalPosition,
  } = useGtmContext();

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scroll(0, 0);
    }
  }, [assetId]);

  useEffect(() => {
    async function fetchData() {
      setDataPublic({
        data: {},
        isError: false,
        isLoading: true,
      });

      switch (assetType) {
        case PUBLIC_PDP_ASSET_TYPES.live: {
          const returnPublicLive = await createBackendServicePublic.get(`/live/schedules?channelId=${assetId}&startTime=${startTimeParam}`, languageEnvironment, regionPublic);
          setDataPublic({
            data: {
              ...returnPublicLive?.data,
              shouldDisplayLockIcon: true,
            },
            isError: (!returnPublicLive.data && returnPublicLive?.code !== 200),
            isLoading: false,
          });
          break;
        }
        default: {
          const returnPublic = await createBackendServicePublic.get(`/asset/${assetId}`, languageEnvironment, regionPublic);
          setDataPublic({
            data: {
              ...returnPublic?.data,
              shouldDisplayLockIcon: true,
            },
            isError: (!returnPublic.data && returnPublic?.code !== 200),
            isLoading: false,
          });
          break;
        }
      }
    }

    if (isPublic) {
      fetchData();
    }
  }, [assetId, assetType, startTimeParam, languageEnvironment, regionPublic, isPublic]);

  const handleClose = () => {
    setGtmContent(null);
    setGtmPlaySource(null);
    setGtmSectionName(null);
    setGtmComponentType(null);
    setGtmCarouselVerticalPosition(null);

    const previousUrl = localStorage.getItem(PREVIOUS_URL) ?? null;
    const landerUrl = localStorage.getItem(LANDER_URL) ?? null;

    if (previousUrl) {
      const referrerUrl = new URL(previousUrl);

      if (referrerUrl.pathname === PATHS.USER_PROFILE) {
        return history.replace(PATHS.HOME);
      }
    }

    return landerUrl ? history.replace(landerUrl) : history.replace(PATHS.HOME);
  };

  const getContent = () => {
    switch (assetType) {
      case PDP_ASSET_TYPES.live:
        return (
          <LiveContent
            key={assetId}
            channelId={assetId}
            resourceType={assetType}
            isPublic={isPublic}
            dataPublic={dataPublic}
          />
        );
      case PDP_ASSET_TYPES.series:
        return (
          <SeriesDetails
            key={assetId}
            assetId={assetId}
            onErrorAction={handleClose}
            isPublic={isPublic}
            dataPublic={dataPublic}
            regionPublic={regionPublic}
          />
        );
      case PDP_ASSET_TYPES.movies:
      case PDP_ASSET_TYPES.vod:
        return (
          <VodContent
            key={assetId}
            assetId={assetId}
            isPublic={isPublic}
            dataPublic={dataPublic}
          />
        );
      default:
        return null;
    }
  };

  const content = getContent();

  const profileSelected = localStorage.getItem(PROFILES_STORAGE.profile);

  if (!isPublic && !profileSelected) {
    savePdpUrlToStorage();

    return <Redirect exact from={pathname} to={PATHS.USER_PROFILE} />;
  }

  return (
    <div className="dtv-details" ref={containerRef}>
      <NavBarPdp forwardedRef={containerRef} />
      <div className="dtv-details-icons">
        {!isPublic && !isMobile && (
          <div className="dtv-details-icons-content">
            <span onClick={handleClose} className="dtv-details-icons-icon dtv-icon-white-close" />
          </div>
        )}
      </div>
      <div className="dtv-details-content">{content}</div>
      <Footer />
    </div>
  );
}

Pdp.propTypes = {
  match: PropTypes.shape({}).isRequired,
};

export default injectIntl(Pdp);
